import React, { useCallback, useEffect, useState } from 'react';
import { IconButton, Badge } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { AppRoutes } from '../../helper/appRoutes';

export const MISC_INBOX_UPDATED_EVENT = 'miscInboxUpdated';

const MISC_UNREAD_POLL_MS = 45 * 1000;

/** Bell badge: always count unreads for the authenticated user only (ignores team-wide query flags). */
function buildBellUnreadQuery(): string {
  const params = new URLSearchParams();
  params.set('onlyMine', '1');
  return `/clientInbox/getMiscInboxUnreadCount?${params.toString()}`;
}

/** Unread count for the header bell; scoped to JWT user via onlyMine (not affected by My Notifications “All team”). */
export function useMiscInboxUnreadCount(): number {
  const { user } = useAuth();
  const token = user?.token;
  const [count, setCount] = useState(0);

  const userId = user?.userId ?? 0;

  const fetchCount = useCallback(() => {
    if (!token || !userId) {
      setCount(0);
      return;
    }
    const q = buildBellUnreadQuery();
    apiClient(q, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((res) => {
        const n = res?.data?.unreadCount;
        setCount(typeof n === 'number' && Number.isFinite(n) ? n : 0);
      })
      .catch(() => setCount(0));
  }, [token, userId]);

  useEffect(() => {
    fetchCount();

    const pollIfVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchCount();
    };

    const intervalId = window.setInterval(pollIfVisible, MISC_UNREAD_POLL_MS);

    const onUpdate = () => fetchCount();
    window.addEventListener(MISC_INBOX_UPDATED_EVENT, onUpdate);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchCount();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(MISC_INBOX_UPDATED_EVENT, onUpdate);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchCount]);

  return count;
}

export const MiscInboxNotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const unread = useMiscInboxUnreadCount();

  return (
    <IconButton
      onClick={() => navigate(AppRoutes.misc.notifications)}
      size="medium"
      title="My Notifications"
      aria-label="My Notifications"
      sx={{
        color: 'white',
        flexShrink: 0,
        mr: 0.5,
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
      }}
    >
      <Badge badgeContent={unread > 0 ? unread : 0} color="error">
        <NotificationsIcon sx={{ fontSize: 24 }} />
      </Badge>
    </IconButton>
  );
};
